import { yupResolver } from '@hookform/resolvers/yup';
import * as M from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { expandServiceSchema } from '../utils/expandServiceSchema';
import useExtendService from '../hooks/useExtendService';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from '../../../../../../shared/queries';
import type { Device } from '../../../../../../shared/types/device';
import { formatDateTimeForApi, getEndOfDay, getLocaleDate, getStartOfDay } from '../../../../../../shared/utils/time';
import { showSuccessNotification } from '../../../../../ui/notification';

interface IExpandServiceFormValues {
  serviceStartDt: Date | null;
  exp: Date | null;
  memo: string;
}

export default function ExpandServiceModal(props: { opened: boolean; onClose: () => void; data: Device }) {
  const { opened, onClose, data } = props;
  const searchParams = useSearchParams();
  const mac = searchParams[0].get('mac') || '';
  const { extendServiceMutate } = useExtendService();
  const queryClient = useQueryClient();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IExpandServiceFormValues>({
    resolver: yupResolver(expandServiceSchema),
    defaultValues: {
      serviceStartDt: null,
      exp: null,
      memo: '',
    },
  });

  const onSubmit = (data: IExpandServiceFormValues) => {
    extendServiceMutate(
      {
        mac,
        serviceStartDt: formatDateTimeForApi(getStartOfDay(data.serviceStartDt!)) || '',
        exp: formatDateTimeForApi(getEndOfDay(data.exp)) || '',
        memo: data.memo,
      },
      {
        onSuccess: () => {
          reset(data);
          showSuccessNotification('변경되었습니다.');
          queryClient.invalidateQueries(queries.devices.extendHistory._def);
          queryClient.invalidateQueries(queries.devices.details._def);
          onClose();
        },
      },
    );
  };

  const onCancel = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (!opened) {
      reset({
        serviceStartDt: data.serviceStartDt ? getLocaleDate(data.serviceStartDt) : null,
        exp: data.exp ? getEndOfDay(getLocaleDate(data.exp)) : null,
        memo: '',
      });
    }
  }, [opened, reset, data]);

  return (
    <M.Modal title="서비스 이용기간 연장" opened={opened} onClose={onCancel}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <M.Group align="flex-end" gap="sm" mb="md">
          <Controller
            name="serviceStartDt"
            control={control}
            render={({ field }) => (
              <DatePickerInput
                flex={1}
                leftSection={<IconCalendar size={14} />}
                placeholder="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                label="서비스 이용기간"
                value={field.value}
                onChange={(value) => field.onChange(getStartOfDay(value))}
                error={errors.serviceStartDt?.message}
              />
            )}
          />
          <M.Text size="sm" component="span" mb="xs">
            -
          </M.Text>
          <Controller
            name="exp"
            control={control}
            render={({ field }) => (
              <DatePickerInput
                flex={1}
                leftSection={<IconCalendar size={14} />}
                placeholder="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                mt={25}
                value={field.value}
                onChange={(value) => field.onChange(getEndOfDay(value))}
                error={errors.exp?.message}
              />
            )}
          />
        </M.Group>
        <M.Textarea label="비고" autosize minRows={5} maxRows={5} {...register('memo')} />
        <M.Group justify="center" mt="xl">
          <M.Button type="button" variant="outline" onClick={onCancel}>
            취소
          </M.Button>
          <M.Button type="submit" variant="filled" color="secondary.5">
            확인
          </M.Button>
        </M.Group>
      </form>
    </M.Modal>
  );
}
